import { Component, OnInit, OnDestroy } from '@angular/core';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, BasicStudentInfo } from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { ErrorService } from '../../services/error/error.service';
import { UserInfo, UserService } from '../../services/user/user.service';

@Component({
    selector: 'app-student-select',
    styleUrls: ['studentselect.component.css'],
    templateUrl: 'studentselect.component.html',
})
export class StudentSelectComponent implements OnInit, OnDestroy {
    private _subRelease: Subject<void> = new Subject<void>();

    // Class
    AllHeaders: string[] = ['Actions', 'StudentID', 'LastName', 'FirstName', 'MiddleName', 'School', 'Grade'];
    LastSizeCode: string;
    PartialViewReady: boolean;
    SelectedStudent: BasicStudentInfo;
    SmallDisplay: boolean;
    StudentChildren: BasicStudentInfo[];
    StudentChildrenData: DataTableSource<BasicStudentInfo>;
    User: UserInfo;
    VisibleColumns: string[];
    VisibleHeaders: string[];

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private mediaChange: ObservableMedia,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.PartialViewReady = false;
        self.SelectedStudent = null;
        self.User = null;

        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
            ? 'sm'
            : self.mediaChange.isActive('md')
            ? 'md'
            : self.mediaChange.isActive('lg')
            ? 'lg'
            : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subRelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.LastSizeCode = media.mqAlias;
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.StudentChildren && self.StudentChildren.length ? self.VisibleHeaders.slice() : ['NoData'];
            });

        self.StudentChildrenData = new DataTableSource<BasicStudentInfo>(self.StudentChildren);

        // We have to wait for the first non-null user object in order to retrieve the list of student children.
        self.userService.UserSubject.pipe(
            takeUntil(self._subRelease),
            first(user => {
                return user && !!user.AuthToken;
            })
        ).subscribe(
            user => {
                self.User = user;

                // Set up constant monitoring of the user object, to catch AuthToken updates.
                self.userService.UserSubject.pipe(takeUntil(self._subRelease)).subscribe(_user => {
                    self.User = _user;
                });

                // Watch for changes in the selected student
                self.appService.SelectedStudentSubject.pipe(takeUntil(self._subRelease)).subscribe(student => {
                    self.SelectedStudent = student;
                });

                // Get the list of student children for this user parent. This should already be loaded by
                // this point, and now we're just retrieving it.
                self.appService
                    .GetStudentsForParent(self.User.AuthToken)
                    .pipe(takeUntil(self._subRelease))
                    .subscribe(
                        children => {
                            self.StudentChildren = children;

                            // We have children found, presumably. Set them up in the table data source.
                            self.StudentChildrenData.Update(self.StudentChildren);

                            // If there's only 1 child, go ahead and select the child. Otherwise, select the first
                            // child in the ordered list.
                            if (!self.appService.SelectedStudent && self.StudentChildren && self.StudentChildren.length) {
                                self.appService.SelectedStudent = self.StudentChildren[0];
                            }

                            // Update visible columns
                            self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                            self.VisibleColumns =
                                !self.StudentChildren || !self.StudentChildren.length ? ['NoData'] : self.VisibleHeaders.slice();

                            // Page is now ready for use.
                            this.PartialViewReady = true;
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            },
            error => {
                self.errorService.HandleServiceError(error);
            }
        );
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    // #region Set Active Student Method

    SetActiveStudent(student: BasicStudentInfo) {
        if (!student) {
            return;
        }

        this.appService.SelectedStudent = student;
    }

    // #endregion
}

// #region Custom Classes

// #endregion



// WEBPACK FOOTER //
// ./src/app/studentselect/studentselect.component.ts